import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError, timeout } from 'rxjs';
import { HttpService } from '@trovata/app/core/services/http.service';
import { environment } from '@trovata/environments/environment';
import {
	Agent,
	AgentDetailResponse,
	AgentInvocationsResponse,
	AgentsResponse,
	CreateAgentResponse,
	InvocationDetailResponse,
	InvokeAgentResponse,
	PatchAgentBody,
} from '@trovata/app/features/ai-agents/models/agents.model';

/** Timeout applied to every HTTP call. All v5 agents handlers are fast DB operations or fire-and-forget Lambda dispatches, so a single 30s budget covers normal latency and Lambda cold-start. */
const HTTP_TIMEOUT_MS: number = 30_000;

/**
 * AgentsHttpService - HTTP service for AI Agents v5 API operations.
 *
 * Provides methods for agent creation, CRUD, and invocation.
 * Maps directly to the rebased v5 Agents OpenAPI endpoints.
 *
 * Base URL uses `environment.trovataAPI('workspace') + '/data/v5/agents'` which resolves to:
 * - Dev: https://api.trovata.io/workspace-dev/data/v5/agents
 * - Beta: https://api.trovata.io/workspace-beta/data/v5/agents
 * - Prod: https://api.trovata.io/workspace/data/v5/agents
 *
 * Note: Chat and preview operations use WebSocket (not HTTP).
 * This is a low-level HTTP service. For state management and
 * business logic, use AgentsFacadeService instead.
 *
 * Timeout policy: core `HttpService` does not apply any timeout, so each
 * method below applies `timeout()` explicitly (30s) and surfaces typed
 * `Error` objects via `catchError`.
 */
@Injectable({
	providedIn: 'root',
})
export class AgentsHttpService {
	// ==========================================
	// Services (injected)
	// ==========================================

	private readonly httpService: HttpService = inject(HttpService);

	// ==========================================
	// Properties
	// ==========================================

	/** Base URL for all agents v5 endpoints. */
	private readonly baseUrl: string = `${environment.trovataAPI('workspace')}/data/v5/agents`;

	// ==========================================
	// Agent Lifecycle Methods
	// ==========================================

	/**
	 * Creates a new temporary (uninstantiated) agent with a fresh sessionId.
	 * The agent is not visible in the list until saved via `updateAgent()`.
	 * Maps `agentId` from the API response to `identifier` for internal use.
	 *
	 * @returns Observable of the temporary agent with a fresh sessionId. Errors: times out after 30s.
	 */
	createAgent(): Observable<Agent> {
		return this.httpService.post(this.baseUrl, { prompt: '', name: 'New Agent' }).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			map(
				(response: CreateAgentResponse): Agent => ({
					identifier: response.agentId,
					sessionId: response.sessionId,
					name: response.name,
					prompt: response.prompt,
				})
			),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'createAgent')))
		);
	}

	// ==========================================
	// Agent CRUD Methods
	// ==========================================

	/**
	 * Retrieves all instantiated agents for the authenticated user.
	 * Returns agent summaries without conversation history or invocation results.
	 *
	 * @returns Observable of the agents list response. Errors: times out after 30s.
	 */
	listAgents(): Observable<AgentsResponse> {
		return this.httpService.get(this.baseUrl).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'listAgents')))
		);
	}

	/**
	 * Retrieves a single agent by ID with full chat history and invocation results.
	 *
	 * @param agentId - The unique identifier of the agent
	 * @returns Observable of the agent detail response wrapper. Errors: times out after 30s.
	 */
	getAgentDetail(agentId: string): Observable<AgentDetailResponse> {
		return this.httpService.get(`${this.baseUrl}/${agentId}`).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'getAgentDetail')))
		);
	}

	/**
	 * Updates an agent's configuration and sets `instantiated=true`.
	 * Only provided fields are updated; `agentId` is required to identify the agent.
	 *
	 * @param request - The patch request containing agentId and optional updates
	 * @returns Observable of the updated agent wrapped in AgentsResponse. Errors: times out after 30s.
	 */
	updateAgent(request: PatchAgentBody): Observable<AgentsResponse> {
		return this.httpService.patch(this.baseUrl, request).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'updateAgent')))
		);
	}

	/**
	 * Permanently deletes an agent and its associated notification rule.
	 *
	 * @param agentId - The unique identifier of the agent to delete
	 * @returns Observable that completes on success (204 No Content). Errors: times out after 30s.
	 */
	deleteAgent(agentId: string): Observable<void> {
		return this.httpService.delete(`${this.baseUrl}/${agentId}`).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'deleteAgent')))
		);
	}

	// ==========================================
	// Execution Methods
	// ==========================================

	/**
	 * Triggers an asynchronous agent invocation. Returns immediately with a
	 * pending invocation record. Poll `getInvocationDetail()` to see results
	 * (`getAgentDetail()` is the fallback for backends without that route).
	 *
	 * @param agentId - The unique identifier of the agent to invoke
	 * @returns Observable of the pending invocation acknowledgment. Errors: times out after 30s.
	 */
	invokeAgent(agentId: string): Observable<InvokeAgentResponse> {
		return this.httpService.post(`${this.baseUrl}/invocation`, { agentId }).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'invokeAgent')))
		);
	}

	/**
	 * Retrieves the 50 most recent invocations across all user agents, optionally filtered to specific agents.
	 *
	 * @param agentIds - Optional list of agent IDs to filter by. When provided, the endpoint receives a
	 *   repeated `agentId` query param (`?agentId=X&agentId=Y`) and returns only invocations for those
	 *   agents. Empty array or `undefined` returns the unfiltered feed.
	 * @returns Observable of the recent invocations response. Errors: times out after 30s.
	 */
	getRecentInvocations(agentIds?: readonly string[]): Observable<AgentInvocationsResponse> {
		const params: HttpParams | undefined = agentIds && agentIds.length > 0 ? new HttpParams({ fromObject: { agentId: [...agentIds] } }) : undefined;
		return this.httpService.get(`${this.baseUrl}/invocations`, params ? { params } : undefined).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'getRecentInvocations')))
		);
	}

	/**
	 * Retrieves a single invocation with its inline-chart payloads.
	 * The list route stays lean; this is the only endpoint that returns `inlineCharts`
	 * (null for runs recorded before charts existed). Unknown ids yield a 404.
	 *
	 * @param invocationId - The unique identifier of the invocation
	 * @returns Observable of the invocation detail response wrapper. Errors: times out after 30s.
	 */
	getInvocationDetail(invocationId: string): Observable<InvocationDetailResponse> {
		return this.httpService.get(`${this.baseUrl}/invocations/${invocationId}`).pipe(
			timeout({ each: HTTP_TIMEOUT_MS }),
			catchError((err: unknown) => throwError(() => this.toHttpError(err, 'getInvocationDetail')))
		);
	}

	// ==========================================
	// Private Methods
	// ==========================================

	/** Wraps arbitrary upstream errors (HttpErrorResponse, TimeoutError, etc.) into a typed `Error` tagged with the operation name. */
	private toHttpError(err: unknown, operation: string): Error {
		const message: string = this.extractErrorMessage(err);
		return new Error(`[AgentsHttp.${operation}] ${message}`);
	}

	/** Extracts a string message from `Error`, `HttpErrorResponse`, or any object with a string `message` field. */
	private extractErrorMessage(err: unknown): string {
		if (err instanceof Error) {
			return err.message;
		}
		if (typeof err === 'object' && err !== null && 'message' in err) {
			const msg: unknown = (err as { message: unknown }).message;
			if (typeof msg === 'string') {
				return msg;
			}
		}
		return 'Unknown error';
	}
}
